import "moment/locale/he";
import moment from "moment/min/moment-with-locales";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import whatsappIcon from "../../../img/svg/whatsapp_icon.svg";
import ScreenHeader from "../../components/ScreenHeader";
import SelectField from "../../components/SelectField";
import Stepper from "../../components/Stepper";
import "./phoneCoordination.scss";

const ALL_MINUTES = ["00", "15", "30", "45"];

// V2 phone-coordination flow (replaces PhoneDialog + the wheel-picker selectDateTime for V2 partners).
// Step 1: call-the-club instructions. Step 2: pick the coordinated date & time.
// All ordering logic stays in the V1 selectDateTime screen, which passes state + handlers as props.
function PhoneCoordination({
  clubName,
  clubPhone,
  values,
  selectedDate,
  setSelectedDate,
  selectedHours,
  onHoursChange,
  selectedMinutes,
  setSelectedMinutes,
  onContinue,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else {
      navigate(-1);
    }
  };

  const cleanPhone = (clubPhone || "").split("-").join("");
  const whatsappPhone = cleanPhone.replace(/^0/, "972");

  const formatDateLabel = (date) => moment(date, "DD/MM/YYYY").locale("he").format("dddd, DD.MM");

  const dateOptions = (values?.dates?.date || []).map((date) => ({
    value: date,
    label: formatDateLabel(date),
  }));

  // Combined HH:mm options. The first hour keeps the already-filtered minute list
  // (V1 removes past times when the selected date is today); later hours allow all quarter-hours.
  const hours = values?.hours?.hour || [];
  const firstHourMinutes = values?.minutes?.minute || ALL_MINUTES;
  const timeOptions = hours.flatMap((hour, index) =>
    (index === 0 ? firstHourMinutes : ALL_MINUTES).map((minute) => ({
      value: `${hour}:${minute}`,
      label: `${hour}:${minute}`,
    })),
  );

  const handleDateSelect = (option) => setSelectedDate({ date: option.value });

  const handleTimeSelect = (option) => {
    const [hour, minute] = option.value.split(":");
    onHoursChange({ hour });
    setSelectedMinutes({ minute });
  };

  // The V1 today-filter can leave selectedHours.hour undefined (e.g. its hour list empties
  // in the last quarter of the day). Self-heal to the first selectable time when one exists;
  // otherwise the field shows a placeholder and the CTA is disabled (user picks another date).
  const hasValidTime = selectedHours?.hour != null && hours.includes(selectedHours.hour);
  useEffect(() => {
    if (!hasValidTime && timeOptions.length > 0) {
      handleTimeSelect(timeOptions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasValidTime, timeOptions.length]);

  if (step === 1) {
    return (
      <div className="pc-v2">
        <ScreenHeader title={clubName} onBack={handleBack} />
        <div className="pc-v2-content">
          <p className="pc-v2-title">{t("PHONE_COORD_TITLE")}</p>
          <p className="pc-v2-subtitle">
            {t("PHONE_COORD_SUBTITLE")} {clubName}.
          </p>

          <div className="pc-v2-contact-row">
            <div className="pc-v2-contact-info">
              <p className="pc-v2-contact-label">{t("PHONE_COORD_PHONE_LABEL")}</p>
              <p className="pc-v2-contact-phone">{clubPhone}</p>
            </div>
            <div className="pc-v2-contact-actions">
              <a
                className="pc-v2-icon-btn pc-v2-icon-btn--whatsapp"
                href={`https://wa.me/${whatsappPhone}`}
                target="_blank"
                rel="noreferrer"
                aria-label="WhatsApp"
              >
                <img src={whatsappIcon} alt="" />
              </a>
              <a className="pc-v2-icon-btn pc-v2-icon-btn--phone" href={`tel:${cleanPhone}`} aria-label={t("PHONE")}>
                <svg viewBox="0 0 19 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M16.73,12.26L14.19,11.97C13.58,11.9 12.98,12.11 12.55,12.54L10.71,14.38C7.88,12.94 5.56,10.63 4.12,7.79L5.97,5.94C6.4,5.51 6.61,4.91 6.54,4.3L6.25,1.78C6.13,0.77 5.28,0.01 4.26,0.01H2.53C1.4,0.01 0.46,0.95 0.53,2.08C1.06,10.62 7.89,17.44 16.42,17.97C17.55,18.04 18.49,17.1 18.49,15.97V14.24C18.5,13.23 17.74,12.38 16.73,12.26Z"
                  />
                </svg>
              </a>
            </div>
          </div>

          <div className="pc-v2-divider" />

          <div className="pc-v2-guide-row">
            <svg className="pc-v2-guide-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="pc-v2-guide-text">{t("PHONE_COORD_GUIDE_1")}</p>
          </div>
          <div className="pc-v2-guide-row">
            <svg className="pc-v2-guide-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="6" y="2" width="12" height="20" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
              <line x1="10" y1="18.5" x2="14" y2="18.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <p className="pc-v2-guide-text">{t("PHONE_COORD_GUIDE_2")}</p>
          </div>
        </div>

        <div className="pc-v2-bottom-bar">
          <Stepper steps={2} current={1} />
          <button type="button" className="pc-v2-cta" onClick={() => setStep(2)}>
            {t("PHONE_COORD_CTA")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pc-v2">
      <ScreenHeader title={clubName} onBack={handleBack} />
      <div className="pc-v2-content">
        <p className="pc-v2-title">{t("PHONE_COORD_STEP2_TITLE", { club: clubName })}</p>

        <div className="pc-v2-selects">
          <SelectField value={formatDateLabel(selectedDate?.date)} options={dateOptions} onSelect={handleDateSelect} />
          <SelectField
            value={hasValidTime ? `${selectedHours.hour}:${selectedMinutes?.minute}` : ""}
            placeholder="--:--"
            options={timeOptions}
            onSelect={handleTimeSelect}
          />
        </div>
      </div>

      <div className="pc-v2-bottom-bar">
        <Stepper steps={2} current={2} />
        <button type="button" className="pc-v2-cta" onClick={onContinue} disabled={!hasValidTime}>
          {t("PHONE_COORD_CONTINUE")}
        </button>
      </div>
    </div>
  );
}

export default PhoneCoordination;
